import { useContext, useState } from 'react';
import { Box } from '@zillow/constellation-10';
import { ChatBubbleWithLinkFeatureToggle } from '@zg-rentals/satellite-components';
import { AppContext } from '../../contexts/AppContextProvider';
import MessageActionMenu from '../MessageActionMenu';
import useFeatureFlagsHelper from '../../hooks/useFeatureFlagsHelper';
import { useUserWarnings } from '../../hooks/useUserWarnings';
import {
  emitExternalLinkClickedInExternalLinkWarningModalEvent,
  emitOpenExternalLinkWarningModalEvent,
} from '../../constants/clickstreamEvents/externalLinkWarningModal';

type ChatBubbleWithActionsProps = {
  senderName?: string;
  variant: 'autoResponse' | 'messageOwner' | 'default';
  messageContent: string;
  isMessageOwner: boolean;
};

export default function ChatBubbleWithActions({
  senderName,
  variant,
  messageContent,
  isMessageOwner,
}: ChatBubbleWithActionsProps) {
  const { isMobileDevice } = useContext(AppContext);
  const { enableMessageLinkInSLW, enableExternalLinkWarningModal } = useFeatureFlagsHelper();

  const { externalLinkWarningData } = useUserWarnings({
    enableExternalLinkWarning: !!enableExternalLinkWarningModal,
  });
  const [isMenuTriggerVisible, setIsMenuTriggerVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleMouseLeave = () => {
    if (!isMenuOpen) {
      setIsMenuTriggerVisible(false);
    }
  };

  const handleMouseEnter = () => {
    // Mouse enter events interfere with touch start events
    if (!isMobileDevice) {
      setIsMenuTriggerVisible(true);
    }
  };

  const handleTouchStart = () => {
    // Hide all other triggers first to prevent multiple triggers from displaying
    const allTriggers = document.querySelectorAll('.trigger-visible');
    allTriggers.forEach((trigger) => {
      trigger.classList.remove('trigger-visible');
    });

    // Reset component state to false and use setTimeout to ensure
    // DOM update happens before setting new state.
    setIsMenuTriggerVisible(false);
    setTimeout(() => {
      setIsMenuTriggerVisible(true);
    }, 0);
  };

  return (
    <Box
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexDirection: isMessageOwner ? 'row-reverse' : 'row',
        gap: 'tight',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
    >
      <ChatBubbleWithLinkFeatureToggle
        senderName={senderName}
        variant={variant}
        enableClickableLinks={!!enableMessageLinkInSLW}
        enableExternalLinkWarningModal={!!enableExternalLinkWarningModal}
        externalLinkWarningData={externalLinkWarningData}
        onExternalLinkWarningModalOpen={emitOpenExternalLinkWarningModalEvent}
        onExternalLinkClickInModal={emitExternalLinkClickedInExternalLinkWarningModalEvent}
        enableHistoryIntegration={true}
      >
        {messageContent}
      </ChatBubbleWithLinkFeatureToggle>
      <MessageActionMenu
        isTriggerVisible={isMenuTriggerVisible}
        messageContent={messageContent}
        setIsMenuOpen={setIsMenuOpen}
        setIsTriggerVisible={setIsMenuTriggerVisible}
      />
    </Box>
  );
}
